import { Helmet } from "react-helmet-async";
import { siteConfig, getCanonicalUrl, getPageTitle } from "@/utils/seo-config";

/**
 * Comprehensive SEO Component
 * Handles meta tags, Open Graph, Twitter Cards, and JSON-LD structured data
 */
function Seo({
  title,
  description,
  image,
  url,
  canonical,
  type = "website", // website, article, product
  keywords,
  author,
  publishedTime,
  modifiedTime,
  article = {}, // For Article schema
  product = {}, // For Product schema
  organization = {}, // For Organization schema
  noindex = false,
  nofollow = false,
  locale = "en_US",
  alternateLanguages = [],
}) {
  // Helper to safely convert to string, filtering out Symbols - MUST BE FIRST
  const safeString = (value, defaultValue = '') => {
    if (value == null) return defaultValue;
    if (typeof value === 'symbol' || typeof value === 'function') return defaultValue;
    try {
      return String(value);
    } catch {
      return defaultValue;
    }
  };

  // Helper to safely get value, ensuring no Symbols
  const safeGet = (value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'symbol' || typeof value === 'function') return fallback;
    return value;
  };

  // Sanitize all inputs immediately - filter out Symbols before any processing
  const safeTitle = safeGet(title);
  const safeDescription = safeGet(description);
  const safeImage = safeGet(image);
  const safeUrl = safeGet(url);
  const safeCanonical = safeGet(canonical);
  const safeKeywords = safeGet(keywords);
  const safeAuthor = safeGet(author);
  const safePublishedTime = safeGet(publishedTime);
  const safeModifiedTime = safeGet(modifiedTime);
  const safeType = typeof type === 'string' ? type : 'website';
  const safeLocale = typeof locale === 'string' ? locale : 'en_US';
  
  // Safely get siteConfig values - wrap in try-catch to handle any Symbols
  const safeSiteTitle = (() => {
    try {
      const value = siteConfig?.title;
      return safeGet(value, '');
    } catch {
      return '';
    }
  })();
  const safeSiteDescription = (() => {
    try {
      const value = siteConfig?.description;
      return safeGet(value, '');
    } catch {
      return '';
    }
  })();
  const safeSiteImage = (() => {
    try {
      const value = siteConfig?.image;
      return safeGet(value, '');
    } catch {
      return '';
    }
  })();
  const safeSiteUrl = (() => {
    try {
      const value = siteConfig?.url;
      return safeGet(value, '');
    } catch {
      return '';
    }
  })();
  const safeSiteKeywords = (() => {
    try {
      const value = siteConfig?.keywords;
      return safeGet(value, '');
    } catch {
      return '';
    }
  })();
  const safeSiteName = (() => {
    try {
      const value = siteConfig?.name;
      return safeGet(value, '');
    } catch {
      return '';
    }
  })();

  // Use defaults from site config and ensure all values are strings
  let pageTitle;
  try {
    const titleValue = safeTitle || safeSiteTitle || '';
    if (titleValue && typeof titleValue !== 'symbol') {
      const pageTitleResult = getPageTitle(titleValue);
      pageTitle = safeString(pageTitleResult || safeSiteTitle || '', safeSiteTitle || '');
    } else {
      pageTitle = safeSiteTitle || '';
    }
  } catch (e) {
    console.warn('Error getting page title:', e);
    pageTitle = safeSiteTitle || '';
  }

  let pageDescription;
  try {
    pageDescription = safeString(safeDescription || safeSiteDescription || '', '');
  } catch {
    pageDescription = safeSiteDescription || '';
  }

  let pageImage;
  try {
    pageImage = safeString(safeImage || safeSiteImage || '', '');
  } catch {
    pageImage = safeSiteImage || '';
  }

  let pageUrl;
  try {
    pageUrl = safeString(safeUrl || (typeof window !== 'undefined' ? window.location.href : safeSiteUrl) || '', '');
  } catch {
    pageUrl = safeSiteUrl || '';
  }

  let canonicalUrl;
  try {
    if (safeCanonical && typeof safeCanonical !== 'symbol') {
      canonicalUrl = safeString(safeCanonical, '');
    } else {
      const path = typeof window !== 'undefined' ? window.location.pathname : '/';
      const canonicalResult = getCanonicalUrl(path);
      canonicalUrl = safeString(canonicalResult || safeSiteUrl || '', safeSiteUrl || '');
    }
  } catch (e) {
    console.warn('Error getting canonical URL:', e);
    canonicalUrl = safeSiteUrl || '';
  }

  const pageKeywords = safeKeywords ? safeString(safeKeywords, '') : (safeSiteKeywords ? safeString(safeSiteKeywords, '') : '');
  const pageAuthor = safeString(safeAuthor || safeSiteName || '', '');
  
  // Safely sanitize article, product, organization objects - ensure they're plain objects
  const safeArticle = (() => {
    if (!article || typeof article !== 'object' || Array.isArray(article)) return {};
    try {
      // Create a clean copy without Symbol properties
      const clean = {};
      const keys = Object.keys(article);
      for (const key of keys) {
        if (typeof key !== 'symbol') {
          const value = article[key];
          if (value !== null && value !== undefined && typeof value !== 'symbol' && typeof value !== 'function') {
            clean[key] = value;
          }
        }
      }
      return clean;
    } catch {
      return {};
    }
  })();
  
  const safeProduct = (() => {
    if (!product || typeof product !== 'object' || Array.isArray(product)) return {};
    try {
      const clean = {};
      const keys = Object.keys(product);
      for (const key of keys) {
        if (typeof key !== 'symbol') {
          const value = product[key];
          if (value !== null && value !== undefined && typeof value !== 'symbol' && typeof value !== 'function') {
            clean[key] = value;
          }
        }
      }
      return clean;
    } catch {
      return {};
    }
  })();
  
  const safeOrganization = (() => {
    if (!organization || typeof organization !== 'object' || Array.isArray(organization)) return {};
    try {
      const clean = {};
      const keys = Object.keys(organization);
      for (const key of keys) {
        if (typeof key !== 'symbol') {
          const value = organization[key];
          if (value !== null && value !== undefined && typeof value !== 'symbol' && typeof value !== 'function') {
            clean[key] = value;
          }
        }
      }
      return clean;
    } catch {
      return {};
    }
  })();
  
  const safeAlternateLanguages = (() => {
    if (!Array.isArray(alternateLanguages)) return [];
    return alternateLanguages.filter(lang => {
      if (!lang || typeof lang !== 'object' || Array.isArray(lang)) return false;
      try {
        const keys = Object.keys(lang);
        // Check if hreflang and href exist and are not Symbols
        return keys.includes('hreflang') && keys.includes('href') &&
               typeof lang.hreflang !== 'symbol' && typeof lang.href !== 'symbol';
      } catch {
        return false;
      }
    });
  })();

  // Robots meta
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  else robotsContent.push('index');
  
  if (nofollow) robotsContent.push('nofollow');
  else robotsContent.push('follow');

  // Helper function to clean object for JSON serialization
  const cleanForJSON = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj === 'symbol' || typeof obj === 'function') return null;
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') return obj;
    
    if (Array.isArray(obj)) {
      return obj
        .map(item => cleanForJSON(item))
        .filter(item => item !== null && item !== undefined && typeof item !== 'symbol');
    }
    
    if (typeof obj === 'object') {
      const cleaned = {};
      try {
        // Use Object.keys to avoid Symbol keys
        const keys = Object.keys(obj);
        for (const key of keys) {
          if (typeof key === 'symbol') continue;
          try {
            const value = obj[key];
            if (value !== null && value !== undefined && typeof value !== 'symbol' && typeof value !== 'function') {
              const cleanedValue = cleanForJSON(value);
              if (cleanedValue !== null && cleanedValue !== undefined) {
                cleaned[safeString(key, '')] = cleanedValue;
              }
            }
          } catch (e) {
            // Skip this property if we can't access it
            continue;
          }
        }
      } catch (e) {
        // If object iteration fails, return empty object
        return {};
      }
      return cleaned;
    }
    
    return null;
  };

  // Declare article properties outside switch so they're accessible later
  let safeArticleAuthor = null;
  let safeArticleSection = null;
  let safeArticleTags = null;
  let safeArticleDatePublished = null;
  let safeArticleDateModified = null;

  // Generate JSON-LD structured data
  let jsonLd = null;

  switch (safeType) {
    case "organization":
      const cleanedOrg = cleanForJSON(safeOrganization);
      const safeSiteLogo = (() => {
        try {
          return safeGet(siteConfig?.logo, '');
        } catch {
          return '';
        }
      })();
      const safeSiteFacebook = (() => {
        try {
          return safeGet(siteConfig?.facebookPage, '');
        } catch {
          return '';
        }
      })();
      const safeSiteInstagram = (() => {
        try {
          return safeGet(siteConfig?.instagramPage, '');
        } catch {
          return '';
        }
      })();
      jsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": safeString(safeSiteName, ""),
        "url": safeString(safeSiteUrl, ""),
        "logo": safeString(safeSiteLogo, ""),
        "sameAs": [
          safeString(safeSiteFacebook, ""),
          safeString(safeSiteInstagram, ""),
        ].filter(url => url && typeof url === 'string'),
        ...(cleanedOrg && typeof cleanedOrg === 'object' ? cleanedOrg : {}),
      };
      break;

    case "article":
      // Safely get article properties - use Object.keys to avoid Symbol properties
      safeArticleAuthor = (() => {
        try {
          if (safeArticle && typeof safeArticle === 'object' && Object.keys(safeArticle).includes('author')) {
            return safeGet(safeArticle.author);
          }
          return null;
        } catch {
          return null;
        }
      })();
      safeArticleSection = (() => {
        try {
          if (safeArticle && typeof safeArticle === 'object' && Object.keys(safeArticle).includes('section')) {
            return safeGet(safeArticle.section);
          }
          return null;
        } catch {
          return null;
        }
      })();
      safeArticleTags = (() => {
        try {
          if (safeArticle && typeof safeArticle === 'object' && Object.keys(safeArticle).includes('tags')) {
            return safeGet(safeArticle.tags);
          }
          return null;
        } catch {
          return null;
        }
      })();
      safeArticleDatePublished = (() => {
        try {
          if (safeArticle && typeof safeArticle === 'object' && Object.keys(safeArticle).includes('datePublished')) {
            return safeGet(safeArticle.datePublished);
          }
          return null;
        } catch {
          return null;
        }
      })();
      safeArticleDateModified = (() => {
        try {
          if (safeArticle && typeof safeArticle === 'object' && Object.keys(safeArticle).includes('dateModified')) {
            return safeGet(safeArticle.dateModified);
          }
          return null;
        } catch {
          return null;
        }
      })();
      
      // Ensure tags is an array of strings
      let articleTags = null;
      if (safeArticleTags) {
        if (Array.isArray(safeArticleTags)) {
          articleTags = safeArticleTags
            .filter(tag => tag != null && typeof tag !== 'symbol' && typeof tag !== 'function' && typeof tag !== 'object')
            .map(tag => safeString(tag, ''))
            .filter(tag => tag)
            .join(', ');
        } else if (typeof safeArticleTags === 'string') {
          articleTags = safeString(safeArticleTags, '');
        }
      }

      // Ensure image is an array of strings
      const articleImage = pageImage 
        ? (Array.isArray(pageImage) 
            ? pageImage.filter(img => img && typeof img !== 'symbol' && typeof img !== 'function').map(img => safeString(img, '')).filter(img => img)
            : [safeString(pageImage, '')].filter(img => img))
        : [];

      const safeArticleImage = articleImage.length > 0 ? articleImage : [safeString(safeSiteImage, '')].filter(img => img);
      const safeArticleLogo = (() => {
        try {
          return safeGet(siteConfig?.logo, '');
        } catch {
          return '';
        }
      })();
      
      jsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": safeString(safeTitle || pageTitle || "", ""),
        "description": safeString(pageDescription || "", ""),
        "image": safeArticleImage,
        "author": {
          "@type": "Person",
          "name": safeString(safeArticleAuthor || pageAuthor || "", ""),
        },
        "publisher": {
          "@type": "Organization",
          "name": safeString(safeSiteName, ""),
          "logo": {
            "@type": "ImageObject",
            "url": safeString(safeArticleLogo, ""),
          },
        },
        "datePublished": safeString(safeArticleDatePublished || safePublishedTime || new Date().toISOString(), ""),
        "dateModified": safeString(safeArticleDateModified || safeModifiedTime || new Date().toISOString(), ""),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": safeString(pageUrl || "", ""),
        },
        ...(safeArticleSection && typeof safeArticleSection === 'string' ? { "articleSection": safeString(safeArticleSection, "") } : {}),
        ...(articleTags && typeof articleTags === 'string' ? { "keywords": articleTags } : {}),
      };
      break;

    case "product":
      const cleanedProduct = cleanForJSON(safeProduct);
      const safeProductImages = safeGet(safeProduct.images);
      const safeProductName = safeGet(safeProduct.name);
      const safeProductDescription = safeGet(safeProduct.description);
      const safeProductSku = safeGet(safeProduct.sku);
      const safeProductId = safeGet(safeProduct.id);
      const safeProductMpn = safeGet(safeProduct.mpn);
      const safeProductBrand = safeGet(safeProduct.brand);
      const safeProductCurrency = safeGet(safeProduct.currency);
      const safeProductPrice = safeGet(safeProduct.price);
      const safeProductAvailability = safeGet(safeProduct.availability);
      const safeProductStockQuantity = safeGet(safeProduct.stock_quantity);
      const safeProductPriceValidUntil = safeGet(safeProduct.priceValidUntil);
      const safeProductRating = safeGet(safeProduct.rating);
      const safeProductReviewCount = safeGet(safeProduct.reviewCount);
      const safeProductReviews = safeGet(safeProduct.reviews);
      const safeProductCategory = safeGet(safeProduct.category);
      
      const productImages = safeProductImages 
        ? (Array.isArray(safeProductImages) 
            ? safeProductImages.filter(img => img && typeof img !== 'symbol' && typeof img !== 'function').map(img => safeString(img, '')).filter(img => img)
            : [safeString(safeProductImages, '')].filter(img => img))
        : [safeString(pageImage, '')].filter(img => img && typeof img !== 'symbol');
      
      jsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": safeString(safeProductName || safeTitle || pageTitle || "", ""),
        "image": productImages.length > 0 ? productImages : [safeString(safeSiteImage, "")].filter(img => img),
        "description": safeString(safeProductDescription || pageDescription || "", ""),
        "sku": safeString(safeProductSku || safeProductId || "FA-001", "FA-001"),
        "mpn": safeString(safeProductMpn || safeProductSku || "", ""),
        "brand": {
          "@type": "Brand",
          "name": safeString(safeProductBrand || safeSiteName || "", ""),
        },
        "offers": {
          "@type": "Offer",
          "url": safeString(pageUrl || "", ""),
          "priceCurrency": safeString(safeProductCurrency || "NGN", "NGN"),
          "price": safeString(safeProductPrice?.toString() || "0", "0"),
          "availability": safeString(safeProductAvailability || (safeProductStockQuantity > 0 
            ? "https://schema.org/InStock" 
            : "https://schema.org/OutOfStock"), "https://schema.org/OutOfStock"),
          "priceValidUntil": safeString(safeProductPriceValidUntil || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], ""),
        },
        ...(safeProductRating && typeof safeProductRating === 'number' && !isNaN(safeProductRating) && {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": Number(safeProductRating),
            "reviewCount": Number(safeProductReviewCount || safeProductReviews || 0),
          },
        }),
        ...(safeProductCategory && typeof safeProductCategory === 'string' ? { "category": safeString(safeProductCategory, "") } : {}),
      };
      // Only merge cleaned product properties that don't conflict
      if (cleanedProduct && typeof cleanedProduct === 'object') {
        Object.keys(cleanedProduct).forEach(key => {
          if (typeof key === 'symbol') return;
          const safeKey = safeString(key, '');
          if (safeKey && !jsonLd.hasOwnProperty(safeKey) && safeKey !== 'name' && safeKey !== 'description' && safeKey !== 'image' && safeKey !== 'sku' && safeKey !== 'mpn' && safeKey !== 'brand' && safeKey !== 'offers' && safeKey !== 'category' && safeKey !== 'rating') {
            const value = cleanedProduct[key];
            if (value !== null && value !== undefined && typeof value !== 'symbol' && typeof value !== 'function') {
              jsonLd[safeKey] = value;
            }
          }
        });
      }
      break;

    case "website":
    default:
      // Default website schema
      jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": safeString(safeSiteName, ""),
        "url": safeString(safeSiteUrl, ""),
        "description": safeString(pageDescription || "", ""),
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": safeString(`${safeSiteUrl}/shop?search={search_term_string}`, ""),
          },
          "query-input": "required name=search_term_string",
        },
      };
      break;
  }
  
  // Final cleanup of jsonLd before rendering
  if (jsonLd) {
    jsonLd = cleanForJSON(jsonLd);
  }

  // Determine OG type
  const ogType = safeType === "article" ? "article" : safeType === "product" ? "product" : "website";

  // Ensure all values are safe strings for rendering (using already sanitized values)
  // Double-check to ensure no Symbols can slip through
  const safePageTitle = (() => {
    const result = safeString(pageTitle, '');
    return typeof result === 'string' ? result : '';
  })();
  const safePageDescription = (() => {
    const result = safeString(pageDescription, '');
    return typeof result === 'string' ? result : '';
  })();
  const safePageImage = (() => {
    const result = safeString(pageImage, '');
    return typeof result === 'string' ? result : '';
  })();
  const safePageUrl = (() => {
    const result = safeString(pageUrl, '');
    return typeof result === 'string' ? result : '';
  })();
  const safeCanonicalUrlFinal = (() => {
    const result = safeString(canonicalUrl, '');
    return typeof result === 'string' ? result : '';
  })();
  const safePageKeywordsFinal = (() => {
    if (!pageKeywords || typeof pageKeywords === 'symbol' || typeof pageKeywords === 'function') return null;
    const result = safeString(pageKeywords, '');
    return typeof result === 'string' && result ? result : null;
  })();
  const safePageAuthorFinal = (() => {
    const result = safeString(pageAuthor, '');
    return typeof result === 'string' ? result : '';
  })();
  const safeOgType = (() => {
    const result = safeString(ogType, 'website');
    return typeof result === 'string' ? result : 'website';
  })();
  const safeSiteNameFinal = (() => {
    const result = safeString(safeSiteName, '');
    return typeof result === 'string' ? result : '';
  })();
  const safeLocaleFinal = (() => {
    const result = safeString(safeLocale, 'en_US');
    return typeof result === 'string' ? result : 'en_US';
  })();

  // Prepare article meta tags safely
  const articleMetaTags = [];
  if (safeType === "article") {
    if (safePublishedTime && typeof safePublishedTime === 'string') {
      const publishedTimeStr = safeString(safePublishedTime, '');
      if (publishedTimeStr && typeof publishedTimeStr === 'string') {
        articleMetaTags.push(<meta key="article-published" property="article:published_time" content={publishedTimeStr} />);
      }
    }
    if (safeModifiedTime && typeof safeModifiedTime === 'string') {
      const modifiedTimeStr = safeString(safeModifiedTime, '');
      if (modifiedTimeStr && typeof modifiedTimeStr === 'string') {
        articleMetaTags.push(<meta key="article-modified" property="article:modified_time" content={modifiedTimeStr} />);
      }
    }
    if (safeArticleAuthor && typeof safeArticleAuthor === 'string') {
      const authorStr = safeString(safeArticleAuthor, '');
      if (authorStr && typeof authorStr === 'string') {
        articleMetaTags.push(<meta key="article-author" property="article:author" content={authorStr} />);
      }
    }
    if (safeArticleSection && typeof safeArticleSection === 'string') {
      const sectionStr = safeString(safeArticleSection, '');
      if (sectionStr && typeof sectionStr === 'string') {
        articleMetaTags.push(<meta key="article-section" property="article:section" content={sectionStr} />);
      }
    }
    if (safeArticleTags) {
      if (Array.isArray(safeArticleTags)) {
        safeArticleTags
          .filter(tag => tag != null && typeof tag === 'string' && typeof tag !== 'symbol' && typeof tag !== 'object' && typeof tag !== 'function')
          .forEach((tag, index) => {
            const tagStr = safeString(tag, '');
            if (tagStr && typeof tagStr === 'string') {
              articleMetaTags.push(<meta key={`article-tag-${index}`} property="article:tag" content={tagStr} />);
            }
          });
      } else if (typeof safeArticleTags === 'string') {
        const tagStr = safeString(safeArticleTags, '');
        if (tagStr && typeof tagStr === 'string') {
          articleMetaTags.push(<meta key="article-tag" property="article:tag" content={tagStr} />);
        }
      }
    }
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{safePageTitle}</title>
      <meta name="title" content={safePageTitle} />
      <meta name="description" content={safePageDescription} />
      {safePageKeywordsFinal && typeof safePageKeywordsFinal === 'string' ? <meta name="keywords" content={safePageKeywordsFinal} /> : null}
      <meta name="author" content={safePageAuthorFinal} />
      <meta name="robots" content={robotsContent.join(', ')} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />

      {/* Canonical URL */}
      <link rel="canonical" href={safeCanonicalUrlFinal} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={safeOgType} />
      <meta property="og:url" content={safePageUrl} />
      <meta property="og:title" content={safePageTitle} />
      <meta property="og:description" content={safePageDescription} />
      <meta property="og:image" content={safePageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={safePageTitle} />
      <meta property="og:site_name" content={safeSiteNameFinal} />
      <meta property="og:locale" content={safeLocaleFinal} />

      {/* Article specific OG tags */}
      {articleMetaTags}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={safePageUrl} />
      <meta name="twitter:title" content={safePageTitle} />
      <meta name="twitter:description" content={safePageDescription} />
      <meta name="twitter:image" content={safePageImage} />
      <meta name="twitter:image:alt" content={safePageTitle} />
      {(() => {
        try {
          const twitterHandleValue = siteConfig?.twitterHandle;
          if (!twitterHandleValue || typeof twitterHandleValue === 'symbol' || typeof twitterHandleValue === 'function') {
            return null;
          }
          const safeTwitterHandle = safeString(twitterHandleValue, '');
          if (safeTwitterHandle && typeof safeTwitterHandle === 'string') {
            return <meta name="twitter:site" content={safeTwitterHandle} />;
          }
          return null;
        } catch {
          return null;
        }
      })()}

      {/* Alternate languages */}
      {safeAlternateLanguages.length > 0
        ? safeAlternateLanguages
            .filter(lang => {
              if (!lang || typeof lang !== 'object' || Array.isArray(lang)) return false;
              try {
                const hreflang = lang.hreflang;
                const href = lang.href;
                return hreflang && href && 
                       typeof hreflang !== 'symbol' && typeof href !== 'symbol' &&
                       typeof hreflang !== 'function' && typeof href !== 'function';
              } catch {
                return false;
              }
            })
            .map((lang, index) => {
              try {
                const hreflang = lang.hreflang;
                const href = lang.href;
                const safeHreflang = safeString(hreflang, '');
                const safeHref = safeString(href, '');
                if (safeHreflang && safeHref && typeof safeHreflang === 'string' && typeof safeHref === 'string') {
                  return (
                    <link
                      key={`alt-lang-${safeString(index, String(index))}`}
                      rel="alternate"
                      hrefLang={safeHreflang}
                      href={safeHref}
                    />
                  );
                }
                return null;
              } catch {
                return null;
              }
            })
            .filter(element => element !== null)
        : null}

      {/* JSON-LD Structured Data */}
      {(() => {
        if (!jsonLd) return null;
        try {
          // Clean the jsonLd object before stringifying
          const cleanedJsonLd = cleanForJSON(jsonLd);
          if (!cleanedJsonLd || typeof cleanedJsonLd !== 'object') return null;
          
          // Use a replacer function to handle any edge cases
          const jsonString = JSON.stringify(cleanedJsonLd, (key, value) => {
            // Filter out symbols, functions, and undefined
            if (typeof value === 'symbol' || typeof value === 'function') {
              return undefined;
            }
            if (value === undefined) {
              return undefined;
            }
            // Skip Symbol keys
            if (typeof key === 'symbol') {
              return undefined;
            }
            // Convert all values to strings where appropriate
            if (value !== null && typeof value === 'object') {
              return value;
            }
            return value;
          }, 2);
          
          if (!jsonString || jsonString === '{}' || jsonString === 'null' || typeof jsonString !== 'string') {
            return null;
          }
          
          return (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonString }} />
          );
        } catch (error) {
          console.error('Error generating JSON-LD:', error);
          return null;
        }
      })()}

      {/* Additional meta tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={safeSiteNameFinal} />
    </Helmet>
  );
}

export default Seo;
